/**
 * Roamzy customer SPA — entry point.
 *
 * Boot orchestration only. All concrete work has been factored into:
 *   - `actions/` — click-action dispatcher + per-domain handlers
 *   - `nav.ts` — auth nav blocks + user dropdown lifecycle
 *   - `runtime-config.ts` — /api/config reactor (payments CTA + banner +
 *     hard-maintenance takeover)
 *   - `data/admin/` — admin SPA (lazy-loaded on /admin paths)
 *   - `routes/` — per-page RouteDef factories + dashboard tabs
 *
 * Phase 2.3 split 2026-05-12.
 */
import './styles/index.css';

// Admin SPA — code-split to its own chunk so customers don't download
// the ~600KB admin bundle on every public page hit. Dynamically imported
// only when path starts with `/admin`. Vite emits a separate chunk
// thanks to the `import('...')` syntax. Operator request 2026-05-06.
if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
  void import('./data/admin/index');
}

import { setLocale, localeFromPath, applyTranslations } from './i18n';
import { PREFIX_ALTERNATION } from './i18n/types';
import { Auth } from './auth';
import { Router } from './router';
import { handleOAuthReturn } from './oauth-return';
import { initWebMcp } from './webmcp';
import { startConfigPoll, onConfigChange, type PublicConfig } from './config';
import { loadExperiments } from './experiments';
import { bootAnalytics } from './analytics';
import { initConsent, onAnalyticsAllowed } from './consent';
import { isInTelegram, autoAuthFromTelegram } from './telegram-webapp';
import {
  haptic as tgHaptic,
  settingsButton as tgSettingsButton,
  tgIsAvailable,
} from './telegram-mini-app';

// Note: the pre-launch gate that once re-routed Top-up clicks here is gone —
// its UI in 2026-05-08, the rest of it 2026-09-04. Backend still respects
// `payments.enabled` + `users.whitelisted` for maintenance mode; unchanged.
import home from './routes/home';
import prices from './routes/prices';
import { guidesHub, guidesIos, guidesAndroid } from './routes/guides';
import faq from './routes/faq';
import blog from './routes/blog';
import blogArticle from './routes/blog-article';
import countryArticle from './routes/country-article';
import { dashboardRoutes } from './routes/dashboard/index';
import { legalTerms, legalPrivacy } from './routes/legal';
import about from './routes/about';
import { agentLandingRoutes } from './routes/agent-landing';
import { priceIndexRoute } from './routes/price-index';
import { glossaryRoute } from './routes/glossary';
import { regionRoute } from './routes/region';
import notFound from './routes/not-found';

// Side-effect modules — registration happens at import time.
//   - `actions/index` registers the global click-action dispatcher
//   - `nav` wires Auth.onChange → renderNavAuth/renderMobileMenuAuth +
//     user-dropdown lifecycle + burger ARIA sync
import './actions/index';
import './nav';
import { applyConfig } from './runtime-config';

// === Locale + translations ============================================
// Boot locale: prefer URL path prefix (`/ru/...` → 'ru'). Falls back to
// 'en' for unprefixed paths. The router re-syncs on every navigation.
setLocale(localeFromPath(window.location.pathname));
// Translate static chrome (nav/footer/cookie/mobile-menu) on first paint.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => applyTranslations());
} else {
  applyTranslations();
}

// === Telegram WebApp mode =============================================
/* Apply `body.is-telegram-webapp` BEFORE first paint when running inside
 * `@roamzy_bot` Mini App. The class drives CSS rules that hide our own
 * nav / footer / cookie-banner — Telegram already provides chrome around
 * the iframe.
 *
 * `WebApp.ready()` tells Telegram we've finished mounting (hides its
 * loading splash). `WebApp.expand()` forces full-height (default is
 * half-screen drawer on iOS).
 *
 * Roamzy keeps its own brand palette inside Telegram — we DON'T mirror
 * Telegram's theme params. Earlier draft did and broke the layout when
 * user's TG was in dark mode (paper bg → black, dark text → invisible).
 * Operator caught 2026-05-09. Brand consistency wins.
 */
if (typeof window !== 'undefined' && window.Telegram?.WebApp?.initData) {
  const tg = window.Telegram.WebApp;
  document.documentElement.classList.add('is-telegram-webapp');
  document.body.classList.add('is-telegram-webapp');
  try { tg.ready?.(); } catch { /* SDK pre-init quirks */ }
  try { tg.expand?.(); } catch { /* unsupported on some hosts */ }
}

// SettingsButton (TG overflow menu → "Settings") routes the user directly
// to /dashboard/settings. Without this wire the menu has the item but
// tapping it is a no-op. Wired once at boot, only inside Telegram.
if (tgIsAvailable()) {
  tgSettingsButton.show(() => {
    tgHaptic.tapLight();
    Router.navigate('/dashboard/settings');
  });
}

// === Auth-gate event from router =======================================
// router.ts dispatches `roamzy:open-login` when an authRequired route is
// hit unauth'd. We open the auth modal with the destination so the modal
// can push it after successful sign-in.
document.addEventListener('roamzy:open-login', (e) => {
  const detail = (e as CustomEvent<{ redirectTo?: string }>).detail;
  // Dynamic import avoids pulling the modal bundle into the LCP path.
  void import('./modals').then(({ openAuth }) => {
    // openAuth's optional `redirectTo` rejects explicit undefined under
    // exactOptionalPropertyTypes — only spread when actually defined.
    openAuth(detail?.redirectTo ? { redirectTo: detail.redirectTo } : {});
  });
});

// === Referral attribution =============================================
// Capture ?ref=CODE first-touch on any landing URL (stored in localStorage,
// bound to the account on the next authenticated dashboard mount).
void import('./referral-attr').then(({ captureRefFromUrl }) => captureRefFromUrl());

// === Router init ======================================================
// /admin paths are owned by the admin SPA (see `import './data/admin/index'`
// above — it self-boots via setTimeout). The customer router would otherwise
// race the admin loader and clobber the mount with a 404 once Auth.init
// resolves.
const onAdminPath = window.location.pathname.startsWith('/admin');

if (!onAdminPath) {
  void Auth.init()
    .then(async () => {
      // If we're inside `@roamzy_bot` Mini App, kick off auto-auth via
      // Telegram.WebApp.initData. No-op outside Telegram. Runs after
      // Auth.init so we don't double-establish the session for returning
      // users with valid cookies.
      if (isInTelegram()) {
        await autoAuthFromTelegram();
      }
      // OAuth consent return (Phase B): if the backend AS bounced an
      // unauthenticated /oauth/authorize here with ?next=, send the user back
      // to the AS once authed (or open login first). Validates `next` is a
      // same-origin AS path — no open redirect. Runs before the heavy article
      // boot so an already-authed user redirects without the 2MB load.
      handleOAuthReturn();
      // WebMCP: expose read-only Roamzy tools to in-browser agents (Claude in
      // Chrome). Feature-detected — a no-op where navigator.modelContext is absent.
      initWebMcp();
      // Render impersonation banner if this session was minted by an
      // admin via the magic-link flow (mig 0024). No-op for normal
      // users.
      const { renderImpersonateBanner } = await import('./impersonate-banner');
      renderImpersonateBanner();
      // Bundle-split (2026-05-08): non-EN article catalogs are loaded
      // lazily — `bootArticles()` fetches the active locale's bodies
      // (~2MB) and parks them in memory before the router renders.
      const { bootArticles } = await import('./data/articles');
      const { ensureFactsLoaded } = await import('./data/facts');
      // CWV gate (2026-07-22): only /blog/:slug and /countries/:slug read
      // the catalog synchronously in render(). Awaiting it on EVERY route
      // held the home hero repaint behind a ~477 KB gzip chunk on slow 4G —
      // measured 6.7 s mobile LCP on `/`. Non-article routes now mount
      // immediately while the catalog loads in the background; an in-app
      // navigation to a page that reads it waits for it (RouteDef.whenReady).
      const catalogReady = Promise.all([bootArticles(), ensureFactsLoaded()]);
      // Country names as the server prints them (utils/country-name.ts): any
      // page outside English shows them in its first render, so wait for the
      // small chunk there; English loads it in the background for a switch.
      const { ensureCountryNames } = await import('./utils/country-name');
      const namesReady = ensureCountryNames();
      if (new RegExp(`^/(?:${PREFIX_ALTERNATION})(?:/|$)`).test(window.location.pathname)) await namesReady;
      else void namesReady.catch(() => { /* Intl names stand in */ });
      // The locale alternation is DERIVED, never hand-written. This line spelled
      // out `ru|es|pt|tr` and silently omitted `zh` from the day Chinese
      // shipped: every /zh/blog/* page mounted its route before the catalog
      // resolved, so `getBlogArticle` found an empty map and rendered the
      // "article does not exist" placeholder on top of a perfectly good SSR
      // body. curl saw the real page, a browser saw the placeholder, and Google
      // — which renders JS — filed the Chinese posts as soft-404s. Found by
      // GSC on 2026-08-17, four months after zh launched.
      const needsCatalog = new RegExp(
        `^(?:/(?:${PREFIX_ALTERNATION}))?/(?:blog|countries)/`,
      ).test(window.location.pathname);
      if (needsCatalog) await catalogReady;
      else void catalogReady;
      // CWV batch 3 (2026-07-22): dashboard/admin/modals CSS (104 KB raw) is
      // split out of the render-blocking bundle — public pages paid for it on
      // the FCP critical path. Vite turns this dynamic import into a separate
      // CSS chunk fetched AFTER first paint. Auth surfaces await it so they
      // never flash unstyled; public routes fire-and-forget.
      const deferredCss = import('./styles/deferred.css');
      if (/^\/(?:dashboard|admin|claim)/.test(window.location.pathname)) {
        await deferredCss;
      } else {
        void deferredCss.catch(() => { /* offline shell — styles arrive with SW */ });
      }
    })
    .then(() => {
      Router.init([
        home,
        prices,
        guidesHub,
        guidesIos,
        guidesAndroid,
        faq,
        blog,
        blogArticle,
        countryArticle,
        ...dashboardRoutes,
        legalTerms,
        legalPrivacy,
        about,
        ...agentLandingRoutes,
        priceIndexRoute,
        glossaryRoute,
        regionRoute,
        notFound,
      ]);
    });
}

// === A/B experiments + analytics ======================================
// Fetch active assignments once at boot. Pages opt in via `getVariant()` /
// `getPayload()` and re-render on `roamzy:exp-loaded` if they painted
// before the response landed.
void loadExperiments();

// Plausible analytics — no-op if VITE_PLAUSIBLE_DOMAIN is unset (local
// dev). Production build sets it via fly.toml [build.args].
initConsent();
// Analytics starts only once the visitor has accepted it — immediately if they
// accepted on an earlier visit, never if they chose "essential only".
onAnalyticsAllowed(() => bootAnalytics());

// === Service worker registration ======================================
/* Caches the SPA shell + hashed /assets/* so the app launches fast on
 * repeat visits and works offline as a real PWA. /api/* is never cached.
 * Gated to production (Vite replaces `import.meta.env.PROD` at build
 * time) so it doesn't interfere with Vite HMR in `pnpm dev`. SW lives
 * at /public/sw.js.
 *
 * Kill switch: in DevTools console, run
 *   navigator.serviceWorker.controller?.postMessage({type:'unregister'})
 * to clear caches and unregister cleanly. */
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  // Register immediately, not on `load` — on iOS PWA the first launch
  // from the Home Screen runs in a fresh browsing context with no prior
  // SW. The earlier we kick off registration, the smaller the window
  // where the user could lose connectivity before precache completes.
  // Operator caught the iOS-PWA-airplane-mode failure 2026-05-09:
  // a fresh Web Clip launched without prior PWA-context online visit
  // had no SW yet, so Safari fell back to its native offline error.
  navigator.serviceWorker.register('/sw.js').catch((err) => {
    // SW failure is non-fatal — app still works without offline support.
    console.warn('[sw] registration failed:', err);
  });

  // Capture beforeinstallprompt so the dashboard banner's "Add to home
  // screen" CTA can trigger the native install dialog with one tap on
  // supported browsers (Chrome/Edge/Brave/Samsung) instead of routing
  // through /guides#pwa. iOS Safari and Firefox fire neither the event
  // nor `appinstalled`; the link fallback covers them.
  void import('./pwa-install').then(({ wireInstallPromptCapture }) => {
    wireInstallPromptCapture();
  });

  /* Auto-enable push notifications when the user installs the PWA.
   * Operator request 2026-05-09: «при установке на Home screen сразу
   * автоматом включать уведомления».
   *
   * Technical reality:
   *   - Android Chrome / Edge / Brave / Samsung Internet fire the
   *     `appinstalled` window event inside a user-gesture chain (the
   *     user just clicked "Install"). That qualifies as a gesture for
   *     `Notification.requestPermission()`.
   *   - iOS Safari does NOT fire `appinstalled`. The user adds via
   *     Share → Add to Home Screen with no JS callback at all. iOS
   *     users still need to tap "Enable alerts" in the dashboard
   *     banner once.
   *
   * Idempotent and silent — if push is unsupported, already granted, or
   * the user previously denied, this no-ops. */
  window.addEventListener('appinstalled', () => {
    void (async () => {
      try {
        const { subscribePush, getPushSupport, getCurrentSubscription } = await import('./push');
        const support = await getPushSupport();
        if (!support.supported) return;
        if (support.permission === 'denied') return;
        const existing = await getCurrentSubscription();
        if (existing) return;
        await subscribePush();
        console.log('[push] auto-subscribed after PWA install');
      } catch (err) {
        console.warn('[push] auto-subscribe after install failed:', err);
      }
    })();
  });

  /* iOS-side fallback: Safari doesn't fire `appinstalled`. To still auto-
   * enable for those users, detect first-ever launch in standalone mode
   * and register a one-shot gesture listener that subscribes on the first
   * user touch. Browsers REQUIRE a gesture for `requestPermission`, so
   * we can't fire it on plain page-load.
   *
   * Single-try guarded by localStorage so we don't keep registering
   * listeners on repeat launches. */
  const FIRST_STANDALONE_KEY = 'roamzy.first-standalone-prompted';
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
  if (isStandalone) {
    let alreadyPrompted = false;
    try { alreadyPrompted = localStorage.getItem(FIRST_STANDALONE_KEY) === '1'; } catch { /* private mode */ }
    if (!alreadyPrompted) {
      const onFirstGesture = (): void => {
        document.removeEventListener('pointerdown', onFirstGesture);
        document.removeEventListener('keydown', onFirstGesture);
        try { localStorage.setItem(FIRST_STANDALONE_KEY, '1'); } catch { /* private mode */ }
        void (async () => {
          try {
            const { subscribePush, getPushSupport, getCurrentSubscription } = await import('./push');
            const support = await getPushSupport();
            if (!support.supported) return;
            if (support.permission === 'denied') return;
            const existing = await getCurrentSubscription();
            if (existing) return;
            await subscribePush();
            console.log('[push] auto-subscribed on first standalone launch');
          } catch (err) {
            console.warn('[push] auto-subscribe on first standalone failed:', err);
          }
        })();
      };
      document.addEventListener('pointerdown', onFirstGesture, { once: true, passive: true });
      document.addEventListener('keydown', onFirstGesture, { once: true });
    }
  }
}

// === Runtime config poll ==============================================
// Boot the poller — runs at module load, kicks first /api/config request
// immediately. Subscribed to onChange so any later flip propagates.
startConfigPoll();
let lastConfig: PublicConfig | null = null;
onConfigChange((c) => { lastConfig = c; applyConfig(c); });
// The banner can be for guests only, so who is signed in is part of the input.
Auth.onChange(() => { if (lastConfig) applyConfig(lastConfig); });

// Re-apply on every SPA navigation — when a new route mounts fresh "Top up"
// buttons we want them disabled out of the gate even if config hasn't
// changed since boot. popstate fires on both history.pushState/back/forward.
window.addEventListener('popstate', () => {
  // Defer one tick so the new route has rendered.
  queueMicrotask(() => { if (lastConfig) applyConfig(lastConfig); });
});
